import { useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FiCheckCircle, FiClock, FiRefreshCw, FiZap } from 'react-icons/fi';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { useI18n } from '../../i18n/useI18n';
import { useAuth } from '../../contexts/AuthContext';
import { createPointsCheckout, getNowpaymentsOptions, getPaymentOrder } from '../../utils/api';

const STATUS_MAP = {
  pending: 'payment.statusPending',
  confirming: 'payment.statusConfirming',
  underpaid: 'payment.statusUnderpaid',
  confirmed: 'payment.statusConfirmed',
  settling: 'payment.statusSettling',
  settled: 'payment.statusSettled',
  expired: 'payment.statusExpired',
  failed: 'payment.statusFailed',
};

const isSuccessStatus = (status, paymentStatus) => {
  const normalized = (status || paymentStatus || '').toLowerCase();
  return ['settled', 'completed', 'confirmed'].includes(normalized);
};

const formatTimeLeft = (ms) => {
  if (ms === null || ms === undefined) return '--:--';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const restMinutes = minutes % 60;
    return `${hours}h ${restMinutes}m`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export function BuyPointsModal({ open, onClose, pushToast, onRequireLogin, onSuccess }) {
  const { t } = useI18n();
  const { isAuthenticated, refreshPoints } = useAuth();

  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedChainId, setSelectedChainId] = useState('');
  const [selectedTokenSymbol, setSelectedTokenSymbol] = useState('');

  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [orderStatus, setOrderStatus] = useState('pending');
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [polling, setPolling] = useState(false);
  const [successHandled, setSuccessHandled] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [nextRefreshIn, setNextRefreshIn] = useState(null);

  const AUTO_REFRESH_SECONDS = 15;
  const AUTO_CLOSE_MS = 1200;
  const MIN_TOPUP_USD = 10;
  const POINTS_PER_USD = 100;
  const [topupAmount, setTopupAmount] = useState(String(MIN_TOPUP_USD));

  const selectedChain = useMemo(() => {
    return paymentOptions.find((chain) => chain.id === selectedChainId) || null;
  }, [paymentOptions, selectedChainId]);

  const selectedTokenPayCurrency = selectedChain?.tokens?.[selectedTokenSymbol];

  const paymentOrderId = checkoutInfo?.payment_order_id;
  const isSuccess = isSuccessStatus(orderStatus, orderInfo?.payment_status);

  const loadPaymentOptions = useCallback(async () => {
    try {
      const res = await getNowpaymentsOptions();
      if (res?.code === 0) {
        const chains = res.data?.chains || [];
        setPaymentOptions(chains);
        const defaultChainId = res.data?.default_chain_id || chains[0]?.id || '';
        const defaultTokenSymbol = res.data?.default_token_symbol ||
          (chains[0]?.tokens ? Object.keys(chains[0].tokens)[0] : '');
        setSelectedChainId(defaultChainId);
        setSelectedTokenSymbol(defaultTokenSymbol);
      } else {
        pushToast?.(res?.msg || t('payment.optionsLoadFailed'), 'error');
      }
    } catch (error) {
      pushToast?.(t('payment.optionsLoadFailed'), 'error');
    }
  }, [pushToast, t]);

  useEffect(() => {
    if (!open) {
      setCheckoutInfo(null);
      setOrderInfo(null);
      setOrderStatus('pending');
      setSuccessHandled(false);
      setTimeLeft(null);
      setNextRefreshIn(null);
      setTopupAmount(String(MIN_TOPUP_USD));
      return;
    }
    setCheckoutInfo(null);
    setOrderInfo(null);
    setOrderStatus('pending');
    setSuccessHandled(false);
    setTimeLeft(null);
    setNextRefreshIn(null);
    setTopupAmount(String(MIN_TOPUP_USD));
    loadPaymentOptions();
  }, [open, loadPaymentOptions]);

  const parsedTopupAmount = useMemo(() => {
    const num = Number(topupAmount);
    return Number.isFinite(num) ? num : NaN;
  }, [topupAmount]);

  const estimatedPoints = useMemo(() => {
    if (!Number.isFinite(parsedTopupAmount)) return null;
    return Math.max(0, Math.floor(parsedTopupAmount * POINTS_PER_USD));
  }, [parsedTopupAmount]);

  const handleCreateCheckout = async () => {
    if (!isAuthenticated) {
      onRequireLogin?.();
      return;
    }
    if (creatingOrder) return;
    if (!Number.isFinite(parsedTopupAmount) || parsedTopupAmount < MIN_TOPUP_USD) {
      pushToast?.(t('points.minTopupHint', { min: MIN_TOPUP_USD }), 'error');
      return;
    }
    setCreatingOrder(true);
    try {
      const payload = { amount: parsedTopupAmount };
      if (selectedTokenPayCurrency) {
        payload.pay_currency = selectedTokenPayCurrency;
      }
      const res = await createPointsCheckout(payload);
      if (res?.code === 0) {
        setCheckoutInfo(res.data);
        setOrderStatus('pending');
      } else {
        pushToast?.(res?.msg || t('payment.createFailed'), 'error');
      }
    } catch (error) {
      pushToast?.(error?.message || t('payment.createFailed'), 'error');
    } finally {
      setCreatingOrder(false);
    }
  };

  const pollOrder = useCallback(async (force = false) => {
    if (!paymentOrderId) return;
    setPolling(true);
    try {
      const res = await getPaymentOrder(paymentOrderId, { refresh: force });
      if (res?.code === 0) {
        const payload = res.data || {};
        const nextStatus = payload.display_status || payload.payment_status || payload.status || 'pending';
        setOrderInfo(payload);
        setOrderStatus(nextStatus);
        setNextRefreshIn(AUTO_REFRESH_SECONDS);
        const success = isSuccessStatus(nextStatus, payload.payment_status);
        if (success && !successHandled) {
          setSuccessHandled(true);
          await refreshPoints();
          onSuccess?.();
          try {
            window.dispatchEvent(new CustomEvent('points-updated'));
          } catch {}
          pushToast?.(t('points.topupSuccess'), 'success');
          window.setTimeout(() => {
            onClose?.();
          }, AUTO_CLOSE_MS);
        }
      } else {
        pushToast?.(res?.msg || t('payment.statusRefreshFailed'), 'error');
      }
    } catch (error) {
      pushToast?.(t('payment.statusRefreshFailed'), 'error');
    } finally {
      setPolling(false);
    }
  }, [paymentOrderId, refreshPoints, pushToast, t, successHandled]);

  useEffect(() => {
    if (!paymentOrderId) return;
    pollOrder();
    const timer = window.setInterval(() => pollOrder(), AUTO_REFRESH_SECONDS * 1000);
    return () => window.clearInterval(timer);
  }, [paymentOrderId, pollOrder]);

  useEffect(() => {
    if (!paymentOrderId) return;
    const timer = window.setInterval(() => {
      setNextRefreshIn((prev) => (prev === null ? prev : Math.max(0, prev - 1)));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [paymentOrderId]);

  useEffect(() => {
    if (!checkoutInfo?.payment?.expires_at) return;
    const expiresAt = Date.parse(checkoutInfo.payment.expires_at);
    if (!Number.isFinite(expiresAt)) return;
    const timer = window.setInterval(() => {
      setTimeLeft(Math.max(0, expiresAt - Date.now()));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [checkoutInfo?.payment?.expires_at]);

  const statusLabelKey = STATUS_MAP[(orderStatus || '').toLowerCase()] || STATUS_MAP.pending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('points.buyTitle')}
      subtitle={t('points.buySubtitle')}
      maxWidthClass="max-w-[760px]"
    >
      {!checkoutInfo ? (
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/50 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white flex items-center gap-2">
                  <FiZap className="text-amber-300" />
                  {t('points.packageTitle')}
                </div>
                <div className="mt-3">
                  <label className="block text-xs text-slate-500 mb-1">{t('payment.amount')} (USD)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={MIN_TOPUP_USD}
                      step="1"
                      value={topupAmount}
                      onChange={(e) => setTopupAmount(e.target.value)}
                      className="w-40 bg-slate-950/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                    />
                    <span className="text-xs text-slate-500">{t('points.minTopupShort', { min: MIN_TOPUP_USD })}</span>
                  </div>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-2xl font-bold text-white">
                  ${Number.isFinite(parsedTopupAmount) ? parsedTopupAmount : '--'}
                </div>
                <div className="text-xs text-slate-500">USD</div>
                <div className="mt-1 text-xs text-slate-400">
                  {t('points.estimated', {
                    points: estimatedPoints === null ? '--' : estimatedPoints.toLocaleString(),
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/40 p-5">
            <div className="text-sm font-semibold text-white mb-3">{t('payment.selectNetwork')}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('payment.network')}</label>
                <select
                  className="w-full bg-slate-950/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                  value={selectedChainId}
                  onChange={(e) => setSelectedChainId(e.target.value)}
                >
                  {paymentOptions.map((chain) => (
                    <option key={chain.id} value={chain.id}>
                      {String(chain.label || chain.id).toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('payment.token')}</label>
                <select
                  className="w-full bg-slate-950/60 border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                  value={selectedTokenSymbol}
                  onChange={(e) => setSelectedTokenSymbol(e.target.value)}
                  disabled={!selectedChain?.tokens}
                >
                  {selectedChain?.tokens
                    ? Object.keys(selectedChain.tokens).map((symbol) => (
                      <option key={symbol} value={symbol}>
                        {symbol}
                      </option>
                    ))
                    : null}
                </select>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateCheckout}
              disabled={
                creatingOrder ||
                !Number.isFinite(parsedTopupAmount) ||
                parsedTopupAmount < MIN_TOPUP_USD
              }
            >
              {creatingOrder ? t('payment.creating') : t('points.buy')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/40 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white">{t('payment.summary')}</div>
                <p className="text-xs text-slate-500 mt-1">
                  {checkoutInfo?.points_amount ? `${checkoutInfo.points_amount.toLocaleString()} ${t('points.balance')}` : null}
                </p>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-white">${checkoutInfo.amount}</div>
                <div className="text-xs text-slate-500">USD</div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="flex items-center justify-center rounded-2xl bg-slate-950/40 border border-slate-700/50 p-4">
                {checkoutInfo?.payment?.pay_uri ? (
                  <QRCodeSVG value={checkoutInfo.payment.pay_uri} size={160} bgColor="transparent" fgColor="#e2e8f0" />
                ) : null}
              </div>
              <div className="space-y-3">
                <div className="text-xs text-slate-500">{t('payment.payAddress')}</div>
                <div className="font-mono text-xs text-slate-200 break-all bg-slate-950/50 border border-slate-700/60 rounded-xl p-3">
                  {checkoutInfo?.payment?.pay_address}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <FiClock className="text-slate-500" />
                  <span>{t('payment.expiresIn')}: {formatTimeLeft(timeLeft)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">{t('payment.statusLabel')}</span>
                  <span className={`font-semibold ${isSuccess ? 'text-emerald-300' : 'text-amber-300'}`}>
                    {isSuccess ? (
                      <span className="inline-flex items-center gap-1">
                        <FiCheckCircle />
                        {t(statusLabelKey)}
                      </span>
                    ) : (
                      t(statusLabelKey)
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => pollOrder(true)}
                    disabled={polling}
                  >
                    <span className="inline-flex items-center gap-2">
                      <FiRefreshCw className={polling ? 'animate-spin' : ''} />
                      {t('payment.refreshStatus')}
                      {nextRefreshIn !== null ? ` (${nextRefreshIn}s)` : ''}
                    </span>
                  </Button>
                  <Button variant="primary" size="small" onClick={onClose}>
                    {t('payment.done')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
